import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { LoadingState } from '@/components/Spinner';
import type { ReviewSessionListResponse, ReviewSessionSummary } from '@/types/review';
import { formatMeetingType, formatSessionDay } from '@/utils/meetingType';

function displayDate(session: ReviewSessionSummary) {
  return session.date || session.latest_evaluated_at?.slice(0, 10) || '-';
}

export function ShadowValidationSessionListPage() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<ReviewSessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dayFilter, setDayFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<ReviewSessionListResponse>('/shadow-validation/sessions')
      .then((resp) => {
        if (cancelled) return;
        setSessions(resp.data.sessions ?? []);
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setError('確認用セッションを取得できませんでした');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dayOptions = useMemo(
    () => Array.from(new Set(sessions.map((session) => session.day).filter(Boolean))).sort(),
    [sessions],
  );
  const displayed = dayFilter ? sessions.filter((session) => session.day === dayFilter) : sessions;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-4">
        <h1 className="text-xl font-semibold text-gray-900">確認用セッション一覧</h1>
        <select
          value={dayFilter}
          onChange={(event) => setDayFilter(event.target.value)}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm"
        >
          <option value="">すべてのDay</option>
          {dayOptions.map((day) => (
            <option key={day} value={day}>
              {formatMeetingType(day)}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center p-8">
          <LoadingState />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <table className="w-full">
            <thead className="border-b bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">実施日</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">クライアント</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">コーチ</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Day</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayed.map((session) => (
                <tr
                  key={session.session_id}
                  onClick={() => navigate(`/shadow-validation/sessions/${encodeURIComponent(session.session_id)}`)}
                  className="cursor-pointer text-gray-700 hover:bg-gray-50"
                >
                  <td className="px-4 py-3 text-sm text-gray-700">{displayDate(session)}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{session.client_name ?? '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{session.coach_name ?? '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{formatSessionDay(session.day, session.day1_variant)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {displayed.length === 0 && (
            <div className="p-8 text-center text-sm text-gray-400">確認用セッションはありません</div>
          )}
        </div>
      )}
      <p className="mt-2 text-xs text-gray-400">{displayed.length} 件表示</p>
    </div>
  );
}
